import React, { useState, useEffect, useCallback } from "react";
import { storage, db, auth } from "../../firebase"; // Import auth directly
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
} from "firebase/storage";
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  increment,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth"; // Import for auth state
import styles from "./MediaPage.module.css";

// Allowed file types and size limits with friendly names
const ALLOWED_FILE_TYPES = [
  { mime: "image/jpeg", extension: ".jpg, .jpeg", icon: "🖼️" },
  { mime: "image/png", extension: ".png", icon: "🖼️" },
  { mime: "image/gif", extension: ".gif", icon: "🖼️" },
  { mime: "video/mp4", extension: ".mp4", icon: "🎬" },
  { mime: "video/quicktime", extension: ".mov", icon: "🎬" },
  { mime: "video/webm", extension: ".webm", icon: "🎬" },
];
const ALLOWED_MIME_TYPES = ALLOWED_FILE_TYPES.map(type => type.mime);
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILE_SIZE_READABLE = "50MB";

// Available categories for organizing media
const CATEGORIES = [
  { id: "", name: "Uncategorized" },
  { id: "Images", name: "Images" },
  { id: "Videos", name: "Videos" },
  { id: "Marketing", name: "Marketing" },
  { id: "Events", name: "Events" },
  { id: "Projects", name: "Projects" },
  { id: "Other", name: "Other" },
];

function MediaPage({ teamId, canUpload, user: propUser }) {
  // Use internal user state that will get populated if propUser is undefined
  const [user, setUser] = useState(propUser);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mediaList, setMediaList] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  
  // Added state for storage tracking
  const [storageUsed, setStorageUsed] = useState(0);
  const [storageLimit, setStorageLimit] = useState(100 * 1024 * 1024); // Default to basic (100MB)
  const [isPro, setIsPro] = useState(false);
  
  const ITEMS_PER_PAGE = 12;

  // Get the authenticated user if not provided
  useEffect(() => {
    if (!propUser) {
      console.log("No user prop provided, getting from Auth");
      const unsubscribe = onAuthStateChanged(auth, (authUser) => {
        if (authUser) {
          console.log("Auth user found:", authUser.email);
          setUser(authUser);
        } else {
          console.log("No authenticated user found");
        }
      });
      
      return () => unsubscribe();
    }
  }, [propUser]);

  // Generate a secure, unique filename
  const generateSecureFilename = (originalName) => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const extension = originalName.split('.').pop();
    return `${timestamp}-${randomString}.${extension}`;
  };

  // Get icon for file type
  const getFileTypeIcon = (fileType) => {
    const matchedType = ALLOWED_FILE_TYPES.find(type => type.mime === fileType);
    return matchedType ? matchedType.icon : "📄";
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + " MB";
    else return (bytes / 1073741824).toFixed(1) + " GB";
  };

  // Validate file before upload
  const validateFile = (file) => {
    if (!file) return "Please select a file";
    if (!ALLOWED_MIME_TYPES.includes(file.type)) 
      return `Invalid file type. Please upload one of the following: ${ALLOWED_FILE_TYPES.map(type => type.extension).join(", ")}`;
    if (file.size > MAX_FILE_SIZE) 
      return `File is too large. Maximum size is ${MAX_FILE_SIZE_READABLE}.`;
    return null;
  };

  // Fetch team data including storage info
  useEffect(() => {
    const fetchTeamAndUserData = async () => {
      try {
        // Fetch team data
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          const teamData = teamSnap.data();
          // Set storage used from team data
          setStorageUsed(teamData.storageUsed || 0);
          
          // Fetch the coach/admin user data to check subscription
          const coachId = teamData.coachId;
          if (coachId) {
            const userRef = doc(db, "users", coachId);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              const userData = userSnap.data();
              // Check if pro based on subscription
              const userIsPro = userData.subscription && 
                              userData.subscription.plan === 'pro';
              setIsPro(userIsPro);
              setStorageLimit(userIsPro ? 
                5 * 1024 * 1024 * 1024 : // 5GB for pro
                100 * 1024 * 1024);      // 100MB for basic
            }
          }
        }
      } catch (err) {
        console.error("Error fetching team and user data:", err);
      }
    };
    
    if (teamId) {
      fetchTeamAndUserData();
    }
  }, [teamId]);

  // File change handler with preview
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    
    const validationError = validateFile(selectedFile);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setFile(selectedFile);
    setError("");
    
    // Create preview
    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      // For video, just show an icon
      setFilePreview(null);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      const validationError = validateFile(droppedFile);
      
      if (validationError) {
        setError(validationError);
        return;
      }
      
      setFile(droppedFile);
      setError("");
      
      if (droppedFile.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target.result);
        reader.readAsDataURL(droppedFile);
      } else {
        setFilePreview(null);
      }
    }
  };

  // Reset the file selection
  const handleResetFile = () => {
    setFile(null);
    setFilePreview(null);
    setError("");
    setSuccess("");
    setCategory("");
  };

  // Improved upload function with progress tracking and storage limit checks
  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    
    // Modified to only check canUpload, not user
    if (!canUpload) {
      setError("You don't have permission to upload files");
      return;
    }
    
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    // Check if upload would exceed storage limit
    if (storageUsed + file.size > storageLimit) {
      if (isPro) {
        setError(`Upload would exceed your Pro storage limit of ${formatFileSize(storageLimit)}. Current usage: ${formatFileSize(storageUsed)}`);
      } else {
        setError(`Upload would exceed your Basic storage limit of ${formatFileSize(storageLimit)}. Current usage: ${formatFileSize(storageUsed)}. Upgrade to Pro for 5GB storage.`);
      }
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setError("");
    setSuccess("");
    
    try {
      // Create a secure filename
      const secureFileName = generateSecureFilename(file.name);
      const storageRef = ref(storage, `teams/${teamId}/media/${secureFileName}`);
      
      // Upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);
      
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Upload failed:", error);
          setError(`Upload failed: ${error.message || "Please try again."}`);
          setUploading(false);
        },
        async () => {
          // Upload successful
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Add to Firestore with metadata
          await addDoc(collection(db, "teamMedia"), {
            teamId,
            url: downloadUrl,
            fileName: secureFileName,
            originalFileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            uploadedBy: user?.uid || 'anonymous',
            uploadedByName: user?.displayName || "Team Member",
            category: category || "Uncategorized",
            createdAt: serverTimestamp(),
            securityToken: `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`,
          });
          
          // After successful upload, update storageUsed in Firestore for team
          const teamRef = doc(db, "teams", teamId);
          await updateDoc(teamRef, {
            storageUsed: increment(file.size)
          });
          
          // Also update the coach's storage usage
          const teamDoc = await getDoc(teamRef);
          if (teamDoc.exists()) {
            const coachId = teamDoc.data().coachId;
            if (coachId) {
              const userRef = doc(db, "users", coachId);
              await updateDoc(userRef, {
                "subscription.storageUsed": increment(file.size)
              });
            }
          }
          
          // Update local state
          setStorageUsed(prevUsed => prevUsed + file.size);
          
          setSuccess("File uploaded successfully!");
          setUploading(false);
          setFile(null);
          setFilePreview(null);
          setUploadProgress(0);
          setCategory("");
          fetchMedia(1, true);
        }
      );
    } catch (err) {
      console.error("Error in upload process:", err);
      setError(`Upload failed: ${err.message || "Please try again."}`);
      setUploading(false);
    }
  };

  // Enhanced fetch media with pagination, search and category filtering
  const fetchMedia = useCallback(async (pageNum = 1, reset = false) => {
    setLoading(true);
    try {
      const mediaRef = collection(db, "teamMedia");
      
      // Build the query with filters
      let mediaQuery = query(
        mediaRef,
        where("teamId", "==", teamId),
        orderBy("createdAt", "desc"),
        limit(ITEMS_PER_PAGE * pageNum)
      );
      
      const querySnapshot = await getDocs(mediaQuery);
      const mediaItems = [];
      
      querySnapshot.forEach((docSnap) => {
        const item = { id: docSnap.id, ...docSnap.data() };
        
        // Apply search filter if needed
        if (searchTerm && !item.originalFileName?.toLowerCase().includes(searchTerm.toLowerCase())) {
          return;
        }
        
        // Apply category filter if needed
        if (filterCategory && item.category !== filterCategory) {
          return;
        }
        
        mediaItems.push(item);
      });
      
      // Get total count for pagination info
      const countQuery = query(mediaRef, where("teamId", "==", teamId));
      const countSnapshot = await getDocs(countQuery);
      setTotalItems(countSnapshot.size);
      
      setMediaList(mediaItems);
      setHasMore(mediaItems.length === ITEMS_PER_PAGE * pageNum);
      
      if (reset) {
        setPage(1);
      } else {
        setPage(pageNum);
      }
    } catch (err) {
      console.error("Error fetching media:", err);
      setError("Failed to load media. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, [teamId, searchTerm, filterCategory]);

  // Updated delete function with storage tracking
  const deleteMedia = async (mediaId, fileName, securityToken) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this media?"
    );
    if (!confirmDelete) return;
    
    if (!canUpload) {
      setError("You don't have permission to delete media");
      return;
    }
    
    try {
      // Get the media item to know its size
      const mediaDoc = await getDoc(doc(db, "teamMedia", mediaId));
      const mediaData = mediaDoc.data();
      const fileSize = mediaData.fileSize || 0;
      
      // Delete from Firestore
      await deleteDoc(doc(db, "teamMedia", mediaId));
      
      // Delete from Storage
      const fileRef = ref(storage, `teams/${teamId}/media/${fileName}`);
      await deleteObject(fileRef);
      
      // Update storage usage for team
      const teamRef = doc(db, "teams", teamId);
      await updateDoc(teamRef, {
        storageUsed: increment(-fileSize)
      });
      
      // Also update the coach's storage usage
      const teamDoc = await getDoc(teamRef);
      if (teamDoc.exists()) {
        const coachId = teamDoc.data().coachId;
        if (coachId) {
          const userRef = doc(db, "users", coachId);
          await updateDoc(userRef, {
            "subscription.storageUsed": increment(-fileSize)
          });
        }
      }
      
      // Update local state
      setStorageUsed(prevUsed => Math.max(0, prevUsed - fileSize));
      
      setSuccess("Media deleted successfully");
      
      // Close modal if we're deleting the selected media
      if (selectedMedia && selectedMedia.id === mediaId) {
        setSelectedMedia(null);
      }
      
      // Refresh the media list
      fetchMedia(page, false);
    } catch (err) {
      console.error("Error deleting media:", err);
      setError("Failed to delete. Please try again.");
    }
  };

  // Secure download with token validation
  const downloadFile = (url, fileName, mediaId) => {
    // Create a download link
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Optional: Log download for analytics
    try {
      addDoc(collection(db, "mediaDownloads"), {
        mediaId,
        teamId,
        downloadedBy: user?.uid || "anonymous",
        downloadedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to log download:", err);
    }
  };

  // Search handler
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Category change handler for upload
  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
  };

  // Category filter handler
  const handleFilterCategoryChange = (e) => {
    setFilterCategory(e.target.value);
  };

  // Modal handlers
  const openModal = (media) => {
    setSelectedMedia(media);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  // Load more handler
  const loadMore = () => {
    if (hasMore) {
      fetchMedia(page + 1);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchMedia(1, true);
  }, [fetchMedia, teamId]);

  // Search and filter effect
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchMedia(1, true);
    }, 500);
    
    return () => clearTimeout(delaySearch);
  }, [searchTerm, filterCategory, fetchMedia]);

  // Auto-hide success/error messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess("");
        setError("");
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Team Media Library</h2>

      {/* Search and filters */}
      <div className={styles.controls}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search media files..."
            value={searchTerm}
            onChange={handleSearch}
            className={styles.searchInput}
            aria-label="Search media files"
          />
        </div>
        
        <div className={styles.filterContainer}>
          <select
            className={styles.filterSelect}
            value={filterCategory}
            onChange={handleFilterCategoryChange}
            aria-label="Filter by category"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Upload section with storage meter */}
      {canUpload && (
        <div 
          className={`${styles.uploadContainer} ${dragActive ? styles.dragActive : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h3 className={styles.subheading}>Upload Media</h3>
          
          {/* Storage usage indicator */}
          <div className={styles.storageInfo}>
            <p className={styles.planInfo}>
              {isPro ? "Pro Plan (5GB)" : "Basic Plan (100MB)"}
            </p>
            <div className={styles.storageBar}>
              <div 
                className={styles.storageUsed}
                style={{ 
                  width: `${Math.min(100, (storageUsed / storageLimit) * 100)}%`,
                  backgroundColor: storageUsed > storageLimit * 0.9 ? '#e74c3c' : '#3498db'
                }}
                role="progressbar"
                aria-valuenow={Math.min(100, (storageUsed / storageLimit) * 100)}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
            <div className={styles.storageText}>
              <div>{formatFileSize(storageUsed)} used of {formatFileSize(storageLimit)}</div>
              {!isPro && (
                <span className={styles.upgradeText}>
                  <a href="/subscription">Upgrade to Pro</a> for 5GB storage
                </span>
              )}
            </div>
          </div>
          
          <div className={styles.uploadInfo}>
            <div className={styles.uploadAllowed}>
              <h4>Acceptable File Types:</h4>
              <ul className={styles.fileTypeList}>
                {ALLOWED_FILE_TYPES.map((type, index) => (
                  <li key={index}>
                    <span className={styles.fileTypeIcon}>{type.icon}</span>
                    <span className={styles.fileTypeExt}>{type.extension}</span>
                  </li>
                ))}
              </ul>
              <p className={styles.fileSizeLimit}>Maximum size: {MAX_FILE_SIZE_READABLE}</p>
            </div>
          </div>
          
          {!file ? (
            <div className={styles.uploadDropzone}>
              <input 
                type="file" 
                id="file-upload" 
                onChange={handleFileChange} 
                className={styles.fileInput}
                accept={ALLOWED_MIME_TYPES.join(",")}
                aria-label="Choose a file to upload"
              />
              <label htmlFor="file-upload" className={styles.fileLabel}>
                <i className={styles.uploadIcon}>📁</i>
                <span>Drag files here or click to browse</span>
              </label>
            </div>
          ) : (
            <div className={styles.filePreviewContainer}>
              {filePreview ? (
                <img src={filePreview} alt="Preview" className={styles.filePreview} />
              ) : (
                <div className={styles.fileIconPreview}>
                  {getFileTypeIcon(file.type)}
                </div>
              )}
              
              <div className={styles.fileInfo}>
                <div className={styles.fileDetails}>
                  <h4 className={styles.fileName}>{file.name}</h4>
                  <p className={styles.fileMeta}>
                    <span className={styles.fileType}>{file.type}</span>
                    <span className={styles.fileSize}>{formatFileSize(file.size)}</span>
                  </p>
                  
                  <div className={styles.categorySelect}>
                    <label htmlFor="category">Category:</label>
                    <select 
                      id="category" 
                      value={category} 
                      onChange={handleCategoryChange}
                      className={styles.select}
                      aria-label="Select category"
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className={styles.fileActions}>
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className={styles.uploadButton}
                    aria-label="Upload file"
                  >
                    {uploading ? "Uploading..." : "Upload File"}
                  </button>
                  
                  <button
                    onClick={handleResetFile}
                    disabled={uploading}
                    className={styles.cancelButton}
                    aria-label="Cancel upload"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {uploading && (
            <div className={styles.progressContainer}>
              <div 
                className={styles.progressBar} 
                style={{ width: `${uploadProgress}%` }}
                role="progressbar"
                aria-valuenow={uploadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
              <span className={styles.progressText}>{uploadProgress}%</span>
            </div>
          )}
        </div>
      )}
      
      {/* Notification area */}
      {(error || success) && (
        <div className={`${styles.notification} ${error ? styles.error : styles.success}`}>
          <span className={styles.notificationIcon}>{error ? '⚠️' : '✅'}</span>
          <span className={styles.notificationMessage}>{error || success}</span>
          <button 
            className={styles.notificationClose} 
            onClick={() => error ? setError('') : setSuccess('')}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}

      {/* Media Gallery */}
      <div className={styles.galleryContainer}>
        <div className={styles.galleryHeader}>
          <h3 className={styles.subheading}>Media Gallery</h3>
          {mediaList.length > 0 && (
            <span className={styles.mediaCount}>
              Showing {mediaList.length} of {totalItems} items
            </span>
          )}
        </div>
        
        {loading && mediaList.length === 0 ? (
          <div className={styles.loading} role="status" aria-live="polite">
            <div className={styles.spinner}></div>
            <p>Loading media...</p>
          </div>
        ) : mediaList.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>
              {searchTerm || filterCategory 
                ? "No media items match your search criteria." 
                : "No media available. Upload some files to get started!"}
            </p>
            
            {(searchTerm || filterCategory) && (
              <button 
                className={styles.clearFiltersButton}
                onClick={() => {
                  setSearchTerm("");
                  setFilterCategory("");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.galleryGrid}>
              {mediaList.map((media) => {
                const isVideo = media.fileType?.startsWith("video/") || 
                               media.fileName?.match(/\.(mp4|mov|webm)$/i);
                               
                return (
                  <div
                    key={media.id}
                    className={styles.galleryItem}
                    onClick={() => openModal(media)}
                  >
                    <div className={styles.previewContainer}>
                      {isVideo ? (
                        <div className={styles.videoPreview}>
                          <i className={styles.videoIcon}>▶️</i>
                          <span className={styles.duration}>Video</span>
                        </div>
                      ) : (
                        <img
                          className={styles.imagePreview}
                          src={media.url}
                          alt={media.originalFileName || media.fileName}
                          loading="lazy"
                        />
                      )}
                    </div>
                    
                    <div className={styles.itemInfo}>
                      <p className={styles.itemName} title={media.originalFileName || media.fileName}>
                        {(media.originalFileName || media.fileName).length > 20 
                          ? (media.originalFileName || media.fileName).substring(0, 20) + "..." 
                          : (media.originalFileName || media.fileName)}
                      </p>
                      
                      <div className={styles.itemMetaContainer}>
                        {media.category && (
                          <span className={styles.itemCategory}>{media.category}</span>
                        )}
                        
                        <div className={styles.itemMeta}>
                          <span className={styles.itemDate}>
                            {media.createdAt?.toDate 
                              ? media.createdAt.toDate().toLocaleDateString() 
                              : "Unknown date"}
                          </span>
                          
                          {media.fileSize && (
                            <span className={styles.itemSize}>
                              {formatFileSize(media.fileSize)}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className={styles.itemButtons}>
                        <button
                          className={styles.downloadButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadFile(
                              media.url, 
                              media.originalFileName || media.fileName,
                              media.id
                            );
                          }}
                          aria-label={`Download ${media.originalFileName || media.fileName}`}
                        >
                          <span className={styles.buttonIcon}>⬇️</span>
                          <span className={styles.buttonText}>Download</span>
                        </button>
                        
                        {canUpload && (
                          <button
                            className={styles.deleteButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteMedia(
                                media.id, 
                                media.fileName,
                                media.securityToken
                              );
                            }}
                            aria-label={`Delete ${media.originalFileName || media.fileName}`}
                          >
                            <span className={styles.buttonIcon}>🗑️</span>
                            <span className={styles.buttonText}>Delete</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {hasMore && (
              <button 
                onClick={loadMore} 
                className={styles.loadMoreButton}
                aria-label="Load more media"
              >
                Load More
              </button>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {selectedMedia && (
        <div 
          className={styles.modalOverlay} 
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="media-modal-title"
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className={styles.modalClose} 
              onClick={closeModal}
              aria-label="Close modal"
            >
              ✕
            </button>
            
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle} id="media-modal-title">
                {selectedMedia.originalFileName || selectedMedia.fileName}
              </h3>
              
              <div className={styles.modalMeta}>
                {selectedMedia.uploadedByName && (
                  <p className={styles.modalUploader}>
                    <span className={styles.modalMetaLabel}>Uploaded by:</span> 
                    {selectedMedia.uploadedByName}
                  </p>
                )}
                
                {selectedMedia.category && (
                  <p className={styles.modalCategory}>
                    <span className={styles.modalMetaLabel}>Category:</span>
                    {selectedMedia.category}
                  </p>
                )}
                
                {selectedMedia.fileSize && (
                  <p className={styles.modalSize}>
                    <span className={styles.modalMetaLabel}>Size:</span>
                    {formatFileSize(selectedMedia.fileSize)}
                  </p>
                )}
                
                {selectedMedia.createdAt?.toDate && (
                  <p className={styles.modalDate}>
                    <span className={styles.modalMetaLabel}>Date:</span>
                    {selectedMedia.createdAt.toDate().toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className={styles.modalBody}>
              {selectedMedia.fileType?.startsWith("video/") || 
               selectedMedia.fileName?.match(/\.(mp4|mov|webm)$/i) ? (
                <video 
                  className={styles.modalVideo} 
                  controls 
                  controlsList="nodownload" 
                  autoPlay
                >
                  <source src={selectedMedia.url} type={selectedMedia.fileType || "video/mp4"} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  className={styles.modalImage}
                  src={selectedMedia.url}
                  alt={selectedMedia.originalFileName || selectedMedia.fileName}
                />
              )}
            </div>
            
            <div className={styles.modalFooter}>
              <button
                className={styles.modalDownload}
                onClick={() => 
                  downloadFile(
                    selectedMedia.url, 
                    selectedMedia.originalFileName || selectedMedia.fileName,
                    selectedMedia.id
                  )
                }
                aria-label={`Download ${selectedMedia.originalFileName || selectedMedia.fileName}`}
              >
                <span className={styles.buttonIcon}>⬇️</span>
                Download
              </button>
              
              {canUpload && (
                <button
                  className={styles.modalDelete}
                  onClick={() => 
                    deleteMedia(
                      selectedMedia.id, 
                      selectedMedia.fileName,
                      selectedMedia.securityToken
                    )
                  }
                  aria-label={`Delete ${selectedMedia.originalFileName || selectedMedia.fileName}`}
                >
                  <span className={styles.buttonIcon}>🗑️</span>
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaPage;